import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useTeam } from '@/hooks/useTeam'
import { useProgramContext } from '@/context/ProgramContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
	Loader2,
	CheckCircle,
	ArrowRight,
	ArrowDown,
	Users,
	FolderPlus,
	Check,
	MessageSquare,
	Bug,
	Lightbulb,
	Star,
	MessageCircle,
	Code2,
	Sparkles,
	Mail,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { config } from '../../config'
import { motion, AnimatePresence } from 'framer-motion'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { getApiBaseUrl } from '@/lib/api'
import { Switch } from '@/components/ui/switch'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import confetti from 'canvas-confetti'
import { WidgetPreview } from '@/components/widget-builder/WidgetPreview'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { widgetApi } from '@/services/api'
import { useTranslation } from '@/hooks/useTranslation'
import { safeStorage } from '@/utils/storage'
import { BRAND_MENTION_PLATFORMS } from '@/types/brandMentions'

function normalizeWebsiteUrl(raw: string): string {
	const t = raw.trim()
	if (!t) return ''
	if (!t.startsWith('http://') && !t.startsWith('https://')) {
		return `https://${t}`
	}
	return t
}

const OnboardingPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { toast } = useToast()
	const queryClient = useQueryClient()
	const { user, loading: authLoading } = useAuth()
	const { teamId, isLoading: teamHookLoading } = useTeam()
	const {
		programs,
		loading: programsHookLoading,
		programId,
		setProgramId,
		fetchPrograms,
	} = useProgramContext()

	type OnboardingStep =
		| 'loading_check'
		| 'welcome'
		| 'team'
		| 'project'
		| 'widget_modules'
		| 'embed_script'
		| 'complete'
		| 'redirecting'
	const [currentStep, setCurrentStep] =
		useState<OnboardingStep>('loading_check')
	const [newTeamName, setNewTeamName] = useState('')
	const [newProgramName, setNewProgramName] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [needsTeam, setNeedsTeam] = useState(false)
	const [teamNameError, setTeamNameError] = useState('')
	const [projectNameError, setProjectNameError] = useState('')
	const [websiteUrl, setWebsiteUrl] = useState('')
	const [websiteUrlError, setWebsiteUrlError] = useState('')
	const [faviconUrl, setFaviconUrl] = useState<string | null>(null)
	const [siteDescription, setSiteDescription] = useState<string | null>(null)
	const [metaLoading, setMetaLoading] = useState(false)
	const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())
	const [isInitialized, setIsInitialized] = useState(false)
	const [createdProgramId, setCreatedProgramId] = useState<string | null>(null)
	const [createdWidgetId, setCreatedWidgetId] = useState<string | null>(null)
	const [embedScript, setEmbedScript] = useState('')

	const [widgetModules, setWidgetModules] = useState({
		enable_contact_messages: true,
		enable_survey: true,
		enable_bug_reporting: true,
		enable_feature_requests: false,
		enable_reviews: false,
		enable_live_chat: false,
		primary_feedback_type: 'nps' as 'nps' | 'csat' | 'custom' | 'testimonial',
	})

	const [selectedLanguage, setSelectedLanguage] = useState('en')
	const [enableMentionsScanner, setEnableMentionsScanner] = useState(false)

	const overallLoading = authLoading || teamHookLoading || programsHookLoading

	// Fetch widgets to check if user has already created one
	const { data: widgetsData, isLoading: widgetsLoading } = useQuery({
		queryKey: ['widgets', programId],
		queryFn: () => {
			if (!programId) {
				return Promise.resolve({ data: [], totalCount: 0 })
			}
			return widgetApi.getWidgets(programId)
		},
		enabled: !!programId,
	})

	const hasWidgets = widgetsData?.data && widgetsData.data.length > 0



	// Create widget preview config from selected modules
	const previewConfig = React.useMemo(() => {
		return {
			name: newProgramName.trim() || t('onboarding.myWidget'),
			enable_contact_messages: widgetModules.enable_contact_messages,
			enable_survey: widgetModules.enable_survey,
			enable_bug_reporting: widgetModules.enable_bug_reporting,
			enable_feature_requests: widgetModules.enable_feature_requests,
			enable_reviews: widgetModules.enable_reviews,
			enable_live_chat: widgetModules.enable_live_chat,
			primary_feedback_type: widgetModules.primary_feedback_type,
			position: 'bottom-right',
			primaryColor: '#866BFF',
			backgroundColor: '#FFFFFF',
			textColor: '#000000',
			feedbackButton: {
				show: true,
				text: t('onboarding.feedback'),
				color: '#866BFF',
				textColor: '#FFFFFF',
			},
			showBranding: true,
			showHeader: true,
			header_title: t('onboarding.shareYourFeedback'),
			question: '',
			buttonText: t('onboarding.submit'),
			thankYou: {
				title: t('onboarding.thankYou'),
				message: t('onboarding.appreciateFeedback'),
			},
			language: selectedLanguage,
		}
	}, [widgetModules, newProgramName, selectedLanguage, t])

	const getAuthHeaders = async () => {
		const {
			data: { session },
		} = await supabase.auth.getSession()
		return {
			Authorization: `Bearer ${session?.access_token}`,
			'Content-Type': 'application/json',
		}
	}

	// Validation functions
	const validateTeamName = (name: string) => {
		if (!name.trim()) {
			setTeamNameError(t('onboarding.teamNameRequired'))
			return false
		}
		if (name.trim().length < 2) {
			setTeamNameError(t('onboarding.teamNameMinLength'))
			return false
		}
		if (name.trim().length > 50) {
			setTeamNameError(t('onboarding.teamNameMaxLength'))
			return false
		}
		setTeamNameError('')
		return true
	}

	const validateProjectName = (name: string) => {
		if (!name.trim()) {
			setProjectNameError(t('onboarding.projectNameRequired'))
			return false
		}
		if (name.trim().length < 2) {
			setProjectNameError(t('onboarding.projectNameMinLength'))
			return false
		}
		if (name.trim().length > 50) {
			setProjectNameError(t('onboarding.projectNameMaxLength'))
			return false
		}
		setProjectNameError('')
		return true
	}

	const validateWebsiteUrlField = (value: string): string | null => {
		const normalized = normalizeWebsiteUrl(value)
		if (!normalized) {
			return t('onboarding.websiteUrlRequired')
		}
		try {
			const url = new URL(normalized)
			const hostname = url.hostname
			if (!hostname.includes('.') || hostname.split('.').length < 2) {
				return t('onboarding.websiteUrlInvalid')
			}
			return null
		} catch {
			return t('onboarding.websiteUrlInvalid')
		}
	}

	const fetchSiteMeta = useCallback(async () => {
		const normalized = normalizeWebsiteUrl(websiteUrl)
		if (!normalized) {
			setSiteDescription(null)
			setFaviconUrl(null)
			return
		}
		try {
			new URL(normalized)
		} catch {
			setSiteDescription(null)
			setFaviconUrl(null)
			return
		}
		setMetaLoading(true)
		try {
			const headers = await getAuthHeaders()
			const res = await fetch(
				`${getApiBaseUrl()}/api/site-meta?url=${encodeURIComponent(normalized)}`,
				{ method: 'GET', cache: 'no-store', headers }
			)
			const data = (await res.json()) as {
				description?: string | null
				faviconUrl?: string | null
			}
			setSiteDescription(data.description ?? null)
			setFaviconUrl(data.faviconUrl ?? null)
		} catch {
			setSiteDescription(null)
			setFaviconUrl(null)
		} finally {
			setMetaLoading(false)
		}
	}, [websiteUrl])

	// Handle input changes with validation
	const handleTeamNameChange = (value: string) => {
		setNewTeamName(value)
		if (value.trim()) {
			validateTeamName(value)
		} else {
			setTeamNameError('')
		}
	}

	const handleProjectNameChange = (value: string) => {
		setNewProgramName(value)
		if (value.trim()) {
			validateProjectName(value)
		} else {
			setProjectNameError('')
		}
	}

	const handleWebsiteUrlChange = (value: string) => {
		setWebsiteUrl(value)
		if (websiteUrlError) setWebsiteUrlError('')
	}

	const handleWebsiteUrlBlur = () => {
		if (!websiteUrl.trim()) {
			setWebsiteUrlError(t('onboarding.websiteUrlRequired'))
			setFaviconUrl(null)
			setSiteDescription(null)
			return
		}
		const err = validateWebsiteUrlField(websiteUrl)
		if (err) {
			setWebsiteUrlError(err)
			setFaviconUrl(null)
			setSiteDescription(null)
			return
		}
		setWebsiteUrlError('')
		void fetchSiteMeta()
	}

	// Calculate progress
	const getProgress = () => {
		const totalSteps = 3
		let currentStepNumber = 0

		if (currentStep === 'project') currentStepNumber = 1
		else if (currentStep === 'widget_modules') currentStepNumber = 2
		else if (currentStep === 'embed_script') currentStepNumber = 3
		else if (currentStep === 'complete') currentStepNumber = totalSteps

		return (currentStepNumber / totalSteps) * 100
	}

	const getStepInfo = () => {
		const totalSteps = 3
		let currentStepNumber = 0

		if (currentStep === 'project') currentStepNumber = 1
		else if (currentStep === 'widget_modules') currentStepNumber = 2
		else if (currentStep === 'embed_script') currentStepNumber = 3
		else if (currentStep === 'complete') currentStepNumber = totalSteps

		return { current: currentStepNumber, total: totalSteps }
	}

	// Initialization effect - runs ONLY ONCE when data is loaded
	useEffect(() => {
		if (!overallLoading && !widgetsLoading && user && !isInitialized) {
			const hasTeam = !!teamId
			const hasProgram = programs.length > 0

			// If user has team, program, and widgets, redirect to dashboard
			if (hasTeam && hasProgram && hasWidgets) {
				setCurrentStep('redirecting')

				// Mark onboarding as complete to prevent redirect loops
				if (user?.id) {
					safeStorage.setOnboardingComplete(user.id)
				}

				navigate('/dashboard', { replace: true })
				return
			}

			// Determine the starting step
			if (!hasTeam || !hasProgram) {
				// User needs to create project (which also creates team if needed)
				// This is step 1
				const userNeedsTeam = !teamId
				setNeedsTeam(userNeedsTeam)
				setCurrentStep('project')
			} else if (hasTeam && hasProgram && !hasWidgets) {
				// User has already completed step 1 (team + program exist)
				// Start at step 2: widget configuration
				const firstProgram = programs[0]
				setCreatedProgramId(firstProgram.id)
				if (firstProgram.name) {
					setNewProgramName(firstProgram.name)
				}
				const w = firstProgram.website_url
				if (w) {
					setWebsiteUrl(
						w.startsWith('http://') || w.startsWith('https://')
							? w
							: `https://${w}`
					)
				}
				if (firstProgram.favicon_url) {
					setFaviconUrl(firstProgram.favicon_url)
				}
				if (firstProgram.site_description) {
					setSiteDescription(firstProgram.site_description)
				}
				setCurrentStep('widget_modules')
			}

			setIsInitialized(true)
		} else if (!overallLoading && !user) {
			// This page is protected, so this case implies an issue or user signed out.
			navigate('/auth', { replace: true })
		}
	}, [
		user,
		teamId,
		programs,
		hasWidgets,
		overallLoading,
		widgetsLoading,
		navigate,
		isInitialized,
	])

	const handleNext = () => {
		if (currentStep === 'team') {
			if (validateTeamName(newTeamName)) {
				setCompletedSteps((prev) => new Set([...prev, 'team']))
				setCurrentStep('project')
			}
		}
	}

	const handleBack = () => {
		if (currentStep === 'project' && needsTeam) {
			setCurrentStep('team')
		}
	}

	const triggerConfetti = () => {
		const count = 200
		const defaults = {
			origin: { y: 0.7 },
			zIndex: 9999,
		}

		function fire(particleRatio: number, opts: any) {
			confetti({
				...defaults,
				...opts,
				particleCount: Math.floor(count * particleRatio),
			})
		}

		fire(0.25, {
			spread: 26,
			startVelocity: 55,
		})

		fire(0.2, {
			spread: 60,
		})

		fire(0.35, {
			spread: 100,
			decay: 0.91,
			scalar: 0.8,
		})

		fire(0.1, {
			spread: 120,
			startVelocity: 25,
			decay: 0.92,
			scalar: 1.2,
		})

		fire(0.1, {
			spread: 120,
			startVelocity: 45,
		})
	}

	const handleCreateWidget = async () => {
		const targetProgramId = createdProgramId || programId

		if (!targetProgramId) {
			toast({
				variant: 'destructive',
				title: t('onboarding.error'),
				description: t('onboarding.noProgramFoundToCreateWidget'),
			})
			return
		}

		setIsSubmitting(true)
		try {
			const headers = await getAuthHeaders()

			const widgetData = {
				name: newProgramName.trim() || t('onboarding.myWidget'),
				program_id: targetProgramId,
				...widgetModules,
				position: 'bottom-right',
				primaryColor: '#866BFF',
				backgroundColor: '#FFFFFF',
				textColor: '#000000',
				feedbackButton: {
					show: true,
					text: t('onboarding.feedback'),
					color: '#866BFF',
					textColor: '#FFFFFF',
				},
				showBranding: true,
				showHeader: true,
				header_title: t('onboarding.shareYourFeedback'),
				question: '',
				buttonText: t('onboarding.submit'),
				thankYou: {
					title: t('onboarding.thankYou'),
					message: t('onboarding.appreciateFeedback'),
				},
				targeting: {
					urlPatterns: [],
					excludeUrlPatterns: [],
					deviceTypes: ['desktop', 'mobile', 'tablet'],
					userProperties: {},
				},
				trigger: {
					type: 'immediate',
				},
				customQuestions: [],
				language: selectedLanguage,
			}

			const widgetResponse = await fetch(`${getApiBaseUrl()}/api/widgets`, {
				method: 'POST',
				headers,
				body: JSON.stringify(widgetData),
			})

			if (!widgetResponse.ok) {
				const errorData = await widgetResponse.json().catch(() => ({}))
				throw new Error(errorData.error || t('onboarding.failedToCreateWidget'))
			}

			const newWidget = await widgetResponse.json()
			setCreatedWidgetId(newWidget.id)

			await fetch(`${getApiBaseUrl()}/api/programs/${targetProgramId}`, {
				method: 'PUT',
				headers,
				body: JSON.stringify({
					brand_mentions_enabled: enableMentionsScanner,
				}),
			})

			const script = `<script src="${window.location.origin}/widget.js" data-widget-id="${newWidget.id}" data-language="${selectedLanguage}"></script>`
			setEmbedScript(script)

			await queryClient.invalidateQueries({ queryKey: ['widgets'] })
			await queryClient.refetchQueries({ queryKey: ['widgets'] })

			setCompletedSteps((prev) => new Set([...prev, 'widget_modules']))
			setCurrentStep('embed_script')

			setTimeout(() => {
				triggerConfetti()
			}, 300)
		} catch (error: any) {
			console.error('handleCreateWidget error:', error)
			toast({
				variant: 'destructive',
				title: t('onboarding.widgetCreationFailed'),
				description: error.message,
			})
		}
		setIsSubmitting(false)
	}

	const handleComplete = async () => {
		if (!validateProjectName(newProgramName)) {
			return
		}
		const webErr = validateWebsiteUrlField(websiteUrl)
		if (webErr) {
			setWebsiteUrlError(webErr)
			return
		}
		setWebsiteUrlError('')
		if (!user) {
			toast({
				variant: 'destructive',
				title: t('onboarding.error'),
				description: t('onboarding.mustBeLoggedIn'),
			})
			return
		}

		setIsSubmitting(true)
		try {
			let currentTeamId = teamId

			// Create team if needed, using the project name as the team name
			if (needsTeam) {
				const headers = await getAuthHeaders()
				const teamResponse = await fetch(`${getApiBaseUrl()}/api/team`, {
					method: 'POST',
					headers,
					body: JSON.stringify({
						name: newProgramName.trim(),
					}),
				})

				if (!teamResponse.ok) {
					const errorData = await teamResponse.json().catch(() => ({}))
					throw new Error(errorData.error || t('onboarding.failedToCreateTeam'))
				}

				const createdTeam = await teamResponse.json()
				currentTeamId = createdTeam.id

				// Invalidate team data to update context for UI
				await queryClient.invalidateQueries({ queryKey: ['team'] })
				await queryClient.refetchQueries({ queryKey: ['team'] })
			}

			const headers = await getAuthHeaders()
			const normalizedSiteUrl = normalizeWebsiteUrl(websiteUrl)
			let resolvedFavicon = faviconUrl
			let resolvedDescription = siteDescription
			try {
				const metaRes = await fetch(
					`${getApiBaseUrl()}/api/site-meta?url=${encodeURIComponent(normalizedSiteUrl)}`,
					{ method: 'GET', cache: 'no-store', headers }
				)
				const metaJson = (await metaRes.json()) as {
					faviconUrl?: string | null
					description?: string | null
				}
				resolvedFavicon = metaJson.faviconUrl ?? null
				resolvedDescription = metaJson.description ?? null
				setFaviconUrl(resolvedFavicon)
				setSiteDescription(resolvedDescription)
			} catch { }

			const programResponse = await fetch(`${getApiBaseUrl()}/api/programs`, {
				method: 'POST',
				headers,
				body: JSON.stringify({
					name: newProgramName.trim(),
					website_url: normalizedSiteUrl,
					favicon_url: resolvedFavicon ?? undefined,
					site_description: resolvedDescription ?? undefined,
				}),
			})

			if (!programResponse.ok) {
				const errorData = await programResponse.json().catch(() => ({}))
				throw new Error(errorData.error || t('onboarding.failedToCreateProgram'))
			}

			const programData = await programResponse.json()
			const newProgram = programData.program || programData

			setCreatedProgramId(newProgram.id)
			setProgramId(newProgram.id)

			await queryClient.invalidateQueries({
				queryKey: ['programs-all'],
			})
			await queryClient.invalidateQueries({
				queryKey: ['widgets'],
			})
			if (currentTeamId) {
				await fetchPrograms(currentTeamId)
			}

			await new Promise(resolve => setTimeout(resolve, 100))

			setCompletedSteps((prev) => new Set([...prev, 'project']))
			setCurrentStep('widget_modules')
		} catch (error: any) {
			console.error('handleComplete error:', error)
			toast({
				variant: 'destructive',
				title: t('onboarding.setupFailed'),
				description: error.message,
			})
		}
		setIsSubmitting(false)
	}

	if (
		currentStep === 'loading_check' ||
		currentStep === 'redirecting' ||
		overallLoading ||
		widgetsLoading
	) {
		return (
			<div className="flex h-screen w-full items-center justify-center bg-gradient-to-b from-slate-100 to-slate-50 dark:from-slate-900 dark:to-slate-800">
				<Loader2 className="h-12 w-12 animate-spin text-primary" />
			</div>
		)
	}

	const stepInfo = getStepInfo()

	return (
		<div className="min-h-screen bg-background p-4 w-full">
			<motion.div
				initial={{ opacity: 0, x: -20 }}
				animate={{ opacity: 1, x: 0 }}
				transition={{ duration: 0.5 }}
				className="absolute top-6 left-6 hidden lg:block">
				<img
					src={config.logo}
					alt={`${config.appName} Logo`}
					className="h-10 w-auto"
				/>
			</motion.div>

			<div className="min-h-screen flex items-center justify-center px-4">
				<motion.div
					initial={{ opacity: 0, scale: 0.95 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.5, delay: 0.2 }}
					className={cn(
						'w-full mx-auto',
						currentStep === 'widget_modules'
							? 'max-w-xl lg:max-w-3xl'
							: 'max-w-xl lg:max-w-2xl'
					)}>
					<Card className="shadow-xl border-border bg-card overflow-hidden">
						<AnimatePresence mode="wait">
							{/* Team Creation Step */}
							{currentStep === 'team' && needsTeam && (
								<motion.div
									key="team"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									transition={{ duration: 0.3 }}>
									{/* Welcome Header */}
									<div className="text-center pt-8 pb-6 px-8 bg-primary">
										<h1 className="text-2xl font-bold text-white">
											{t('onboarding.welcomeTo', { appName: config.appName })} 🎉
										</h1>
									</div>
									{stepInfo.total > 1 && (
										<div className="px-8 pt-6 pb-4">
											<div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
												<span>
													{t('onboarding.stepOf', { current: stepInfo.current, total: stepInfo.total })}
												</span>
												<span>{Math.round(getProgress())}% {t('onboarding.complete')}</span>
											</div>
											<Progress value={getProgress()} className="h-2" />
										</div>
									)}
									<div className="px-8 py-4">
										<div className="flex items-center gap-3">
											<div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
												<Users className="h-6 w-6 text-primary" />
											</div>
											<div>
												<h2 className="text-xl font-bold text-primary">
													{t('onboarding.createYourTeam')}
												</h2>
												<p className="text-muted-foreground text-sm mt-1">
													{t('onboarding.setupTeamWorkspace')}
												</p>
											</div>
										</div>
									</div>
									<CardContent className="px-8 pb-8">
										<div className="space-y-6">
											<div className="space-y-3">
												<Label
													htmlFor="team-name"
													className="text-sm font-medium">
													{t('onboarding.teamName')} *
												</Label>
												<Input
													id="team-name"
													value={newTeamName}
													onChange={(e) => handleTeamNameChange(e.target.value)}
													placeholder={t('onboarding.teamNamePlaceholder')}
													disabled={isSubmitting}
													className={cn(
														'h-12 border-2 text-base transition-colors',
														teamNameError
															? 'border-destructive focus-visible:ring-destructive'
															: newTeamName.trim() && !teamNameError
																? 'border-green-500 focus-visible:ring-green-500'
																: ''
													)}
												/>
												{teamNameError && (
													<p className="text-sm text-destructive">
														{teamNameError}
													</p>
												)}
												{newTeamName.trim() && !teamNameError && (
													<p className="text-sm text-green-600 flex items-center">
														<Check className="h-4 w-4 mr-1" />
														{t('onboarding.looksGood')}
													</p>
												)}
												<p className="text-xs text-muted-foreground">
													{newTeamName.length}/50 {t('onboarding.characters')}
												</p>
											</div>

											<div className="flex space-x-3">
												<Button
													onClick={handleBack}
													variant="outline"
													className="flex-1 h-12">
													{t('onboarding.back')}
												</Button>
												<Button
													onClick={handleNext}
													disabled={!newTeamName.trim() || !!teamNameError}
													className="flex-1 h-12">
													<ArrowRight className="mr-2 h-4 w-4" />
													{t('onboarding.continue')}
												</Button>
											</div>
										</div>
									</CardContent>
								</motion.div>
							)}

							{/* Project Creation Step */}
							{currentStep === 'project' && (
								<motion.div
									key="project"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									transition={{ duration: 0.3 }}>
									{/* Welcome Header */}
									<div className="text-center pt-8 pb-6 px-8 bg-primary">
										<h1 className="text-2xl font-bold text-white">
											{t('onboarding.welcomeTo', { appName: config.appName })} 🎉
										</h1>
									</div>
									{stepInfo.total > 1 && (
										<div className="px-8 pt-6 pb-4">
											<div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
												<span>
													{t('onboarding.stepOf', { current: stepInfo.current, total: stepInfo.total })}
												</span>
												<span>{Math.round(getProgress())}% {t('onboarding.complete')}</span>
											</div>
											<Progress value={getProgress()} className="h-2" />
										</div>
									)}
									<div className="px-8 py-4">
										<div className="flex items-center gap-3">

											<h2 className="text-xl font-bold text-primary">
												{t('onboarding.createYourFirstProject')}
											</h2>
										</div>
									</div>
									<CardContent className="px-8 pb-8">
										<div className="space-y-6">
											<div className="space-y-3">
												<div className="bg-primary/10 dark:bg-primary/20 border mb-4 border-primary/20 rounded-lg p-4">
													<p className="text-sm text-primary dark:text-primary">{t('onboarding.projectNameHint')}
													</p>
												</div>
												<Label
													htmlFor="project-name"
													className="text-sm font-medium">
													{t('onboarding.projectName')} *
												</Label>

												<Input
													id="project-name"
													value={newProgramName}
													onChange={(e) =>
														handleProjectNameChange(e.target.value)
													}
													placeholder={t('onboarding.projectNamePlaceholder')}
													disabled={isSubmitting}
													className={cn(
														'h-8 border-2 px-2 text-base transition-colors',
														projectNameError
															? 'border-destructive focus-visible:ring-destructive'
															: newProgramName.trim() && !projectNameError
																? 'border-green-500 focus-visible:ring-green-500'
																: ''
													)}
													onKeyPress={(e) =>
														e.key === 'Enter' &&
														!isSubmitting &&
														newProgramName.trim() &&
														!projectNameError &&
														handleComplete()
													}
												/>
												{projectNameError && (
													<p className="text-sm text-destructive">
														{projectNameError}
													</p>
												)}
												{newProgramName.trim() && !projectNameError && (
													<p className="text-sm text-green-600 flex items-center">
														<Check className="h-4 w-4 mr-1" />
														{t('onboarding.perfect')}
													</p>
												)}
												<p className="text-xs text-muted-foreground">
													{newProgramName.length}/50 {t('onboarding.characters')}
												</p>
											</div>

											<div className="space-y-3">
												<Label
													htmlFor="project-website-url"
													className="text-sm font-medium">
													{t('onboarding.websiteUrl')} *
												</Label>
												<div className="relative">
													{faviconUrl ? (
														<img
															src={faviconUrl}
															alt=""
															className="pointer-events-none absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 object-contain"
															onError={() => setFaviconUrl(null)}
															referrerPolicy="no-referrer"
														/>
													) : null}
													<Input
														id="project-website-url"
														type="url"
														value={websiteUrl}
														onChange={(e) =>
															handleWebsiteUrlChange(e.target.value)
														}
														onBlur={() => handleWebsiteUrlBlur()}
														placeholder={t('onboarding.websiteUrlPlaceholder')}
														disabled={isSubmitting}
														className={cn(
															'h-8 border-2 px-2 text-base transition-colors',
															faviconUrl && 'pl-8',
															websiteUrlError
																? 'border-destructive focus-visible:ring-destructive'
																: ''
														)}
													/>
												</div>
												{websiteUrlError && (
													<p className="text-sm text-destructive">
														{websiteUrlError}
													</p>
												)}
												{metaLoading && (
													<p className="text-xs text-muted-foreground">
														{t('onboarding.loadingSiteDescription')}
													</p>
												)}
												{!metaLoading && siteDescription ? (
													<div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-foreground">
														{siteDescription}
													</div>
												) : null}
											</div>

											<div className="flex justify-end">
												<Button
													onClick={handleComplete}
													disabled={
														isSubmitting ||
														!newProgramName.trim() ||
														!!projectNameError ||
														!websiteUrl.trim() ||
														!!websiteUrlError
													}
													className="h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 px-8">
													{isSubmitting ? (
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													) : (
														<CheckCircle className="mr-2 h-4 w-4" />
													)}
													{isSubmitting ? t('onboarding.creating') : t('onboarding.createProject')}
												</Button>
											</div>
										</div>
									</CardContent>
								</motion.div>
							)}

							{/* Widget Modules Step */}
							{currentStep === 'widget_modules' && (
								<motion.div
									key="widget_modules"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									transition={{ duration: 0.3 }}>
									{/* Welcome Header */}
									<div className="text-center pt-8 pb-6 px-8 bg-primary">
										<h1 className="text-2xl font-bold text-white">
											{t('onboarding.welcomeTo', { appName: config.appName })} 🎉
										</h1>
									</div>
									{stepInfo.total > 1 && (
										<div className="px-8 pt-6 pb-4">
											<div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
												<span>
													{t('onboarding.stepOf', { current: stepInfo.current, total: stepInfo.total })}
												</span>
												<span>{Math.round(getProgress())}% {t('onboarding.complete')}</span>
											</div>
											<Progress value={getProgress()} className="h-2" />
										</div>
									)}
									<div className="px-8 py-4">
										<div className="flex items-center gap-3">

											<div>
												<h2 className="text-xl font-bold text-primary">
													{t('onboarding.configureYourWidget')}
												</h2>
												<p className="text-muted-foreground text-sm mt-1">
													{t('onboarding.chooseFeedbackTypes')}
												</p>
											</div>
										</div>
									</div>
									<CardContent className="px-8 pb-8">
										<div className="space-y-6">
											<div className="space-y-4 rounded-lg border bg-muted/30 p-2">
												<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
													<div className="flex items-start justify-between gap-3 rounded-lg border p-4">
														<div className="flex min-w-0 items-center space-x-3">
															<Mail className="h-5 w-5 shrink-0 text-muted-foreground" />
															<div className="min-w-0">
																<p className="font-medium">{t('onboarding.mailbox')}</p>
																<p className="text-sm text-muted-foreground">
																	{t('onboarding.mailboxDescription')}
																</p>
															</div>
														</div>
														<Switch
															checked={widgetModules.enable_contact_messages}
															onCheckedChange={(checked) =>
																setWidgetModules((prev) => ({
																	...prev,
																	enable_contact_messages: checked,
																}))
															}
														/>
													</div>

													<div className="flex items-start justify-between gap-3 rounded-lg border p-4">
														<div className="flex min-w-0 items-center space-x-3">
															<Bug className="h-5 w-5 shrink-0 text-muted-foreground" />
															<div className="min-w-0">
																<p className="font-medium">{t('onboarding.bugReporting')}</p>
																<p className="text-sm text-muted-foreground">
																	{t('onboarding.allowUsersReportBugs')}
																</p>
															</div>
														</div>
														<Switch
															checked={widgetModules.enable_bug_reporting}
															onCheckedChange={(checked) =>
																setWidgetModules((prev) => ({
																	...prev,
																	enable_bug_reporting: checked,
																}))
															}
														/>
													</div>

													<div className="flex items-start justify-between gap-3 rounded-lg border p-4">
														<div className="flex min-w-0 items-center space-x-3">
															<Lightbulb className="h-5 w-5 shrink-0 text-muted-foreground" />
															<div className="min-w-0">
																<p className="font-medium">{t('onboarding.featureSuggestions')}</p>
																<p className="text-sm text-muted-foreground">
																	{t('onboarding.letUsersSuggestVote')}
																</p>
															</div>
														</div>
														<Switch
															checked={widgetModules.enable_feature_requests}
															onCheckedChange={(checked) =>
																setWidgetModules((prev) => ({
																	...prev,
																	enable_feature_requests: checked,
																}))
															}
														/>
													</div>

													<div className="flex items-start justify-between gap-3 rounded-lg border p-4">
														<div className="flex min-w-0 items-center space-x-3">
															<Star className="h-5 w-5 shrink-0 text-muted-foreground" />
															<div className="min-w-0">
																<p className="font-medium">{t('onboarding.reviews')}</p>
																<p className="text-sm text-muted-foreground">
																	{t('onboarding.collectProductServiceReviews')}
																</p>
															</div>
														</div>
														<Switch
															checked={widgetModules.enable_reviews}
															onCheckedChange={(checked) =>
																setWidgetModules((prev) => ({
																	...prev,
																	enable_reviews: checked,
																}))
															}
														/>
													</div>

													<div className="flex items-start justify-between gap-3 rounded-lg border p-4">
														<div className="flex min-w-0 items-center space-x-3">
															<MessageCircle className="h-5 w-5 shrink-0 text-muted-foreground" />
															<div className="min-w-0">
																<p className="font-medium">{t('onboarding.liveChat')}</p>
																<p className="text-sm text-muted-foreground">
																	{t('onboarding.talkWithUsersRealtime')}
																</p>
															</div>
														</div>
														<Switch
															checked={widgetModules.enable_live_chat}
															onCheckedChange={(checked) =>
																setWidgetModules((prev) => ({
																	...prev,
																	enable_live_chat: checked,
																}))
															}
														/>
													</div>

													<div className="flex flex-col gap-3 rounded-lg border p-4">
														<div className="flex items-center justify-between gap-3">
															<div className="flex min-w-0 items-center space-x-3">
																<MessageSquare className="h-5 w-5 shrink-0 text-muted-foreground" />
																<div className="min-w-0">
																	<p className="font-medium">{t('onboarding.collectFeedbackSurveys')}</p>
																	<p className="text-sm text-muted-foreground">
																		{t('onboarding.gatherGeneralFeedback')}
																	</p>
																</div>
															</div>
															<Switch
																checked={widgetModules.enable_survey}
																onCheckedChange={(checked) =>
																	setWidgetModules((prev) => ({
																		...prev,
																		enable_survey: checked,
																	}))
																}
															/>
														</div>
														{widgetModules.enable_survey && (
															<div className="space-y-2">
																<Label className="text-sm">{t('onboarding.primaryFeedbackType')}</Label>
																<select
																	value={widgetModules.primary_feedback_type}
																	onChange={(e) =>
																		setWidgetModules((prev) => ({
																			...prev,
																			primary_feedback_type: e.target.value as
																				| 'nps'
																				| 'csat'
																				| 'custom'
																				| 'testimonial',
																		}))
																	}
																	className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm">
																	<option value="nps">{t('onboarding.npsSurvey')}</option>
																	<option value="csat">{t('onboarding.csatSurvey')}</option>
																	<option value="custom">{t('onboarding.customQuestions')}</option>
																	<option value="testimonial">{t('onboarding.testimonialReviewForm')}</option>
																</select>
															</div>
														)}
													</div>
												</div>

												<div className="flex items-center justify-between gap-4">
													<Label className="shrink-0 text-sm font-medium">
														{t('onboarding.widgetLanguage')}
													</Label>
													<Select
														value={selectedLanguage}
														onValueChange={setSelectedLanguage}>
														<SelectTrigger className="w-48">
															<SelectValue placeholder={t('onboarding.selectLanguage')} />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="en">🇬🇧 {t('onboarding.english')}</SelectItem>
															<SelectItem value="fr">🇫🇷 {t('onboarding.french')}</SelectItem>
															<SelectItem value="de">🇩🇪 {t('onboarding.german')}</SelectItem>
															<SelectItem value="es">🇪🇸 {t('onboarding.spanish')}</SelectItem>
															<SelectItem value="zh">🇨🇳 {t('onboarding.chinese')}</SelectItem>
															<SelectItem value="km">🇰🇭 {t('onboarding.khmer')}</SelectItem>
															<SelectItem value="vi">🇻🇳 {t('onboarding.vietnamese')}</SelectItem>
														</SelectContent>
													</Select>
												</div>
											</div>
											<div className="flex items-center justify-between gap-4 rounded-lg border p-4">
												<div className="min-w-0 space-y-2">
													<p className="font-medium">
														{t('onboarding.enableMentionsScanner')}
													</p>
													<div className="flex items-center gap-1.5">
														{BRAND_MENTION_PLATFORMS.map(
															(platform) => (
																<PlatformIcon
																	key={platform}
																	platform={platform}
																	className="h-4 w-4"
																	colored
																/>
															)
														)}
													</div>
													<p className="text-sm text-muted-foreground">
														{t('onboarding.enableMentionsScannerPlans')}
													</p>
												</div>
												<Switch
													checked={enableMentionsScanner}
													onCheckedChange={setEnableMentionsScanner}
												/>
											</div>
											<div className="flex justify-end">
												<Button
													onClick={handleCreateWidget}
													disabled={isSubmitting}
													className="h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 px-8">
													{isSubmitting ? (
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													) : (
														<Sparkles className="mr-2 h-4 w-4" />
													)}
													{isSubmitting ? t('onboarding.creatingWidget') : t('onboarding.createWidget')}
												</Button>
											</div>


										</div>
									</CardContent>
								</motion.div>
							)}

							{/* Embed Script Step */}
							{currentStep === 'embed_script' && (
								<motion.div
									key="embed_script"
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.5 }}>
									{/* Progress Header */}
									<div className="text-center pt-8 pb-6 px-8 bg-primary">
										<h1 className="text-2xl font-bold text-white">
											{t('onboarding.welcomeTo', { appName: config.appName })} 🎉
										</h1>
									</div>
									{stepInfo.total > 1 && (
										<div className="px-8 pt-6 pb-4">
											<div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
												<span>
													{t('onboarding.stepOf', { current: stepInfo.current, total: stepInfo.total })}
												</span>
												<span>{Math.round(getProgress())}% {t('onboarding.complete')}</span>
											</div>
											<Progress value={getProgress()} className="h-2" />
										</div>
									)}
									<CardContent className="px-8 pb-8 pt-0 text-center">
										<motion.div
											initial={{ scale: 0 }}
											animate={{ scale: 1 }}
											transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
											className="w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
											<CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
										</motion.div>
										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.4 }}>
											<h2 className="text-2xl font-bold text-card-foreground mb-3">
												🎉 {t('onboarding.widgetCreated')}
											</h2>
											<p className="text-muted-foreground mb-6">
												{t('onboarding.addCodeToWebsite')}
											</p>

											<div className="bg-slate-900 dark:bg-slate-800 rounded-lg p-4 mb-6 text-left">
												<div className="flex items-center justify-between mb-2">
													<div className="flex items-center space-x-2">
														<Code2 className="h-4 w-4 text-slate-400" />
														<span className="text-sm text-slate-400">{t('onboarding.embedCode')}</span>
													</div>
													<Button
														size="sm"
														variant="ghost"
														onClick={() => {
															navigator.clipboard.writeText(embedScript)
															toast({
																title: t('onboarding.copied'),
																description: t('onboarding.embedCodeCopied'),
															})
														}}
														className="text-slate-400 hover:text-white">
														{t('onboarding.copy')}
													</Button>
												</div>
												<pre className="text-sm text-green-400 overflow-x-auto">
													<code>{embedScript}</code>
												</pre>
											</div>

											<p className="text-sm text-muted-foreground mb-6">
												💡 {t('onboarding.customizeWidgetAnytime')}
											</p>

											<div className="flex flex-col sm:flex-row gap-3 justify-center">
												<Button
													onClick={() => navigate(`/widgets/edit/${createdWidgetId}`)}
													variant="outline"
													className="h-12 px-6">
													<Sparkles className="mr-2 h-4 w-4" />
													{t('onboarding.customizeWidget')}
												</Button>
												<Button
													onClick={async () => {
														// Ensure cache is fully updated before navigating
														const targetProgramId = createdProgramId || programId
														if (targetProgramId) {
															await queryClient.refetchQueries({
																queryKey: ['widgets', targetProgramId],
																type: 'active'
															})
														}

														// Mark onboarding as complete to prevent redirect loops
														if (user?.id) {
															safeStorage.setOnboardingComplete(user.id)
														}

														// Navigate with state to indicate we're coming from onboarding
														navigate('/dashboard', {
															replace: true,
															state: { fromOnboarding: true }
														})
													}}
													className="h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 px-6">
													<ArrowRight className="mr-2 h-4 w-4" />
													{t('onboarding.accessDashboard')}
												</Button>
											</div>
										</motion.div>
									</CardContent>
								</motion.div>
							)}

							{/* Completion Step */}
							{currentStep === 'complete' && (
								<motion.div
									key="complete"
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.5 }}>
									<CardContent className="p-12 text-center">
										<motion.div
											initial={{ scale: 0 }}
											animate={{ scale: 1 }}
											transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
											className="w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
											<CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
										</motion.div>
										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.4 }}>
											<h2 className="text-2xl font-bold text-card-foreground mb-3">
												🎉 {t('onboarding.allSet')}
											</h2>
											<p className="text-muted-foreground mb-6">
												{t('onboarding.workspaceCreatedSuccessfully')} <br />
												{t('onboarding.redirectingToDashboard')}
											</p>
											<div className="flex items-center justify-center">
												<Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
												<span className="text-sm text-muted-foreground">
													{t('onboarding.takingYouToDashboard')}
												</span>
											</div>
										</motion.div>
									</CardContent>
								</motion.div>
							)}
						</AnimatePresence>
					</Card>


				</motion.div>
			</div>

			{currentStep === 'widget_modules' &&
				(widgetModules.enable_contact_messages ||
					widgetModules.enable_survey ||
					widgetModules.enable_bug_reporting ||
					widgetModules.enable_feature_requests ||
					widgetModules.enable_reviews ||
					widgetModules.enable_live_chat) && (
					<>
						<div className="pointer-events-none fixed bottom-16 right-6 z-[2147483001] hidden flex-col items-end gap-1 lg:flex">
							<p className=" whitespace-pre-line text-right text-sm font-medium text-foreground">
								{t('onboarding.widgetPreviewLabel')}
							</p>
							<ArrowDown className="h-7 w-7 text-primary" />
						</div>
						<div className="hidden lg:block">
							<WidgetPreview
								config={previewConfig as any}
								showManualTrigger={true}
							/>
						</div>
					</>
				)}
		</div>
	)
}

export default OnboardingPage